import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { getMyOrders, trackOrder } from '../services/orderApi';
import { useCurrency } from '../context/CurrencyContext';

const MyOrders = () => {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const { getLocalizedPath } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const token =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('token')
          : null;

      setLoading(true);
      setError('');
      try {
        let ordersList = [];

        if (token) {
          // Logged in user flow
          const params = new URLSearchParams({
            page: '1',
            limit: '20',
            sortBy: 'createdAt',
            sortOrder: 'desc',
          });
          const response = await getMyOrders(params);
          if (response?.success && response?.data) {
            ordersList = Array.isArray(response.data) ? response.data : [];
          } else if (Array.isArray(response)) {
            ordersList = response;
          }
        } else {
          // Guest user flow - fetch from local storage
          const guestOrderIds = JSON.parse(localStorage.getItem('guest_orders') || '[]');
          if (guestOrderIds.length > 0) {
            // Fetch all guest orders in parallel
            const results = await Promise.all(
              guestOrderIds.map(id => trackOrder(id).catch(e => null))
            );
            // Filter valid responses (trackOrder returns the order object directly)
            ordersList = results.filter(order => order && order.orderNumber);

            // Sort by date descending
            ordersList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          }
        }

        setOrders(ordersList);
      } catch (e) {
        setError(e.message || 'Failed to load your orders');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to get status color
  const getStatusColor = (status, paymentStatus) => {
    if (paymentStatus === 'paid' && status === 'processing')
      return 'text-green-600 bg-green-50';
    if (paymentStatus === 'paid' && status === 'confirmed')
      return 'text-blue-600 bg-blue-50';
    if (status === 'delivered') return 'text-green-600 bg-green-50';
    if (status === 'cancelled') return 'text-red-600 bg-red-50';
    if (paymentStatus === 'pending') return 'text-yellow-600 bg-yellow-50';
    if (paymentStatus === 'failed') return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  // Helper function to get payment status display
  const getPaymentStatusDisplay = (paymentStatus) => {
    const statusMap = {
      pending: 'Payment Pending',
      paid: 'Paid',
      failed: 'Payment Failed',
      cancelled: 'Cancelled',
    };
    return statusMap[paymentStatus] || paymentStatus;
  };

  return (
    <section className="mt-[78px] px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1100px] mx-auto py-10">
        <h1 className="text-[28px] sm:text-[36px] font-semibold mb-6">
          {t('orders.myOrders') || 'My Orders'}
        </h1>
        {loading && <div className="py-16 text-center">Loading...</div>}
        {error && !loading && (
          <div className="py-16 text-center text-red-600">{error}</div>
        )}
        {/* Orders List */}
        {!loading && !error && (
          <div className="space-y-4">
            {orders.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📦</div>
                <p className="text-gray-500 text-lg">
                  {t('orders.none') || 'You have no orders yet.'}
                </p>
                <Link
                  to={getLocalizedPath('/shop')}
                  className="inline-block mt-4 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition"
                >
                  {t('orders.startShopping') || 'Start Shopping'}
                </Link>
              </div>
            )}
            {orders.map((order) => (
              <div
                key={order._id || order.id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-white"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Order Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-gray-900">
                        {order.orderNumber || 'N/A'}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          order.status,
                          order.paymentStatus
                        )}`}
                      >
                        {order.status?.charAt(0).toUpperCase() +
                          order.status?.slice(1) || 'Unknown'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">
                          {t('orders.items') || 'Items'}:
                        </span>{' '}
                        {order.items?.length || 0}
                      </div>
                      <div>
                        <span className="font-medium">
                          {t('trackOrder.orderDate') || 'Date'}:
                        </span>{' '}
                        {formatDate(order.createdAt)}
                      </div>
                      <div>
                        <span className="font-medium">
                          {t('checkout.payment') || 'Payment'}:
                        </span>
                        <span
                          className={`ml-1 ${order.paymentStatus === 'paid'
                            ? 'text-green-600'
                            : order.paymentStatus === 'failed'
                              ? 'text-red-600'
                              : 'text-yellow-600'
                            }`}
                        >
                          {t(`checkout.status.${order.paymentStatus}`) ||
                            getPaymentStatusDisplay(order.paymentStatus)}
                        </span>
                      </div>
                      {order.payment?.method && (
                        <div>
                          <span className="font-medium">
                            {t('trackOrder.paymentMethod') || 'Method'}:
                          </span>{' '}
                          {order.payment.method.replace('_', ' ').toUpperCase()}
                        </div>
                      )}
                      {order.payment?.last4 && (
                        <div>
                          <span className="font-medium">
                            {t('orders.card') || 'Card'}:
                          </span>{' '}
                          **** {order.payment.last4} ({order.payment.brand})
                        </div>
                      )}
                      <div>
                        <span className="font-medium">
                          {t('orders.currency') || 'Currency'}:
                        </span>{' '}
                        {order.currency || 'USD'}
                      </div>
                    </div>

                    {/* Order Items Preview */}
                    {order.items && order.items.length > 0 && (
                      <div className="mt-3">
                        <div className="text-sm text-gray-500 mb-1">
                          {t('orders.items') || 'Items'}:
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {order.items.slice(0, 3).map((item, index) => (
                            <span
                              key={index}
                              className="text-xs bg-gray-100 px-2 py-1 rounded"
                            >
                              {item.product?.name ||
                                item.productName ||
                                'Unknown Item'}{' '}
                              × {item.quantity}
                              {item.color && (
                                <span className="text-gray-500">
                                  {' '}
                                  ({item.color})
                                </span>
                              )}
                              {item.size && (
                                <span className="text-gray-500">
                                  {' '}
                                  - {item.size}
                                </span>
                              )}
                            </span>
                          ))}
                          {order.items.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{order.items.length - 3}{' '}
                              {t('common.more') || 'more'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Price and Action */}
                  <div className="flex items-center justify-between lg:flex-col lg:items-end gap-3">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        {formatPrice(order.totalAmount ?? 0)}
                      </div>
                      {order.currency && order.currency !== 'USD' && (
                        <div className="text-sm text-gray-500">
                          {order.currency}
                        </div>
                      )}
                    </div>

                    <Link
                      to={getLocalizedPath(
                        `/track-your-order?order=${encodeURIComponent(
                          order.orderNumber || ''
                        )}`
                      )}
                      className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-medium"
                    >
                      {t('orders.trackOrder') || 'Track Order'}
                    </Link>
                  </div>
                </div>

                {/* Shipping Address & Tracking (if available) */}
                {(order.shippingAddress || order.shipment) && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      {order.shippingAddress && (
                        <div className="text-gray-600">
                          <span className="font-medium">
                            {t('orders.shippingTo') || 'Shipping to'}:
                          </span>
                          <div className="mt-1">
                            {order.shippingAddress.firstName}{' '}
                            {order.shippingAddress.lastName},<br />
                            {order.shippingAddress.city},{' '}
                            {order.shippingAddress.state}{' '}
                            {order.shippingAddress.zipCode}
                            <br />
                            {order.shippingAddress.country}
                          </div>
                        </div>
                      )}

                      {/* DHL Tracking Information */}
                      {order.shipment && (
                        <div className="text-gray-600">
                          <span className="font-medium">
                            {t('orders.trackingInfo') || 'Tracking Info'}:
                          </span>
                          <div className="mt-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-red-600 font-bold">
                                DHL
                              </span>
                              {order.shipment.status && (
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-medium ${order.shipment.status === 'created' ||
                                    order.shipment.status === 'shipped'
                                    ? 'bg-green-100 text-green-700'
                                    : order.shipment.status === 'failed'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-gray-100 text-gray-700'
                                    }`}
                                >
                                  {order.shipment.status
                                    ?.charAt(0)
                                    .toUpperCase() +
                                    order.shipment.status?.slice(1)}
                                </span>
                              )}
                            </div>
                            {order.shipment.trackingNumber && (
                              <div className="mb-1">
                                <span className="font-medium">
                                  {t('trackOrder.tracking') || 'Tracking #'}:
                                </span>
                                <a
                                  href={`https://www.dhl.com/en/express/tracking.html?AWB=${order.shipment.trackingNumber}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 text-blue-600 hover:underline font-mono text-xs"
                                >
                                  {order.shipment.trackingNumber}
                                </a>
                              </div>
                            )}
                            {order.shipment.pieceTrackingNumbers &&
                              order.shipment.pieceTrackingNumbers.length >
                              0 && (
                                <div className="text-xs text-gray-500">
                                  {t('orders.pieces') || 'Pieces'}:{' '}
                                  {order.shipment.pieceTrackingNumbers.join(
                                    ', '
                                  )}
                                </div>
                              )}
                            {order.shipment.createdAt && (
                              <div className="text-xs text-gray-500">
                                {t('trackOrder.shipped') || 'Shipped'}:{' '}
                                {formatDate(order.shipment.createdAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MyOrders;
